/**
 * Resets everything a pubwise bundle (the "DT wrapper" + Prebid) leaves behind so that the NEXT bundle
 * can take over cleanly, without anyone having to edit the bundles.
 *
 * What a bundle leaves behind, and why each step is needed:
 *  - the wrapper's AdManager (timers, registry of ad units): cleared through the wrapper's own
 *    `DTWrapper.queue.push(cmd)` API, which hands `cmd` the live manager;
 *  - `window.pwRegisterLazyLoad`: the wrapper only installs its shim when this is not a function, so a
 *    stale shim would keep serving the previous bundle's ad units forever;
 *  - Prebid ad units / auctions: dropped, but the `pbjs` instance and its `que` are KEPT. Prebid is
 *    loaded once per document (a second copy aborts on `libLoaded`) and, once loaded, `que.push`
 *    executes immediately; replacing `que` with a plain array would silently drop every callback;
 *  - the previous bundle's <script> tags.
 */
export const tearDownPubwise = (w: Window = window) => {
  w.document?.querySelectorAll('script[data-pubwise]').forEach((el) => el.remove())

  const wrapper = w.DTWrapper
  try {
    // Only a live queue (an object, once the wrapper initialized) runs the command right away.
    if (wrapper?.queue && !Array.isArray(wrapper.queue)) {
      wrapper.queue.push((manager: any) => {
        if (manager?.batchTimer) {
          clearTimeout(manager.batchTimer)
        }
        manager.batchTimer = null
        manager.batchQueue = []
        manager.registry?.clear?.()
      })
    }
  } catch (e) {
    console.log('Error resetting the pubwise wrapper', e)
  }
  // A fresh array: the next bundle's wrapper adopts it and replaces it with its own live queue.
  w.DTWrapper = { queue: [] }
  delete w.pwRegisterLazyLoad
  delete w.pubwiseLazyLoad

  w.pbjs = w.pbjs || ({} as Window['pbjs'])
  w.pbjs.que = w.pbjs.que || []
  try {
    w.pbjs.removeAdUnit?.()
    w.pbjs.clearAllAuctions?.()
  } catch (e) {
    console.log('Error resetting prebid', e)
  }

  enablePubwise(w)
}

// Slots route through the prebid que (see refreshAdd) only while this is set.
export const enablePubwise = (w: Window = window) => {
  w.pubwise = {
    enabled: true,
  };
}

// Pubwise could not be loaded: let slots fall back to plain GAM instead of waiting on the prebid que.
export const disablePubwise = () => {
  window.pubwise = {
    enabled: false,
  };
}

const buildDntGuardedScripts = (srcs: (string | undefined)[]): string => {
  const valid = srcs.filter(Boolean) as string[];
  if (!valid.length) { return ''; }
  const loaders = valid
    .map((src) => { return `var s=document.createElement('script');s.async=true;s.src=${JSON.stringify(src)};document.head.appendChild(s);`; })
    .join('');
  return `<script>if(navigator.doNotTrack!=='1'){${loaders}}</script>`;
};

export const getScriptListString = (globalData: Record<string, any>) => {
  if (globalData && Object.values(globalData)[0]?.adScriptList) {
    const adList = Object.values(globalData)[0]?.adScriptList;
    return adList?.reduce(
      (_prev: any, next: { pubwisePreScript: any; pubwiseScript: any }) => {
        return buildDntGuardedScripts([next.pubwisePreScript, next.pubwiseScript]);
      },
      ''
    );
  }

  if (globalData?.commonData?.defaultAdData) {
    const adConfig: AdConfig = globalData.commonData?.defaultAdData;
    return adConfig?.adTag
      ? buildDntGuardedScripts([adConfig.adTag.pubwisePreScript, adConfig.adTag.pubwiseScript])
      : '';
  }

  return '';
};
